<template>
	<div
		class="supplier-performance-world-map"
		@mouseleave="scheduleClearHover"
	>
		<svg
			class="world-map-svg"
			:viewBox="world.viewBox"
			role="img"
			aria-label="Supplier performance world map"
		>
			<defs>
				<clipPath
					v-for="clip in splitClipPaths"
					:key="clip.key"
					:id="clip.id"
					clipPathUnits="objectBoundingBox"
				>
					<rect :x="clip.x" y="0" :width="clip.width" height="1" />
				</clipPath>
			</defs>

			<template v-for="location in world.locations">
				<template v-if="isSplitCountry(location.id)">
					<path
						v-for="(slice, index) in getSplitSlices(location.id)"
						:key="`${location.id}-slice-${index}`"
						:d="location.path"
						class="world-map-region world-map-region--split"
						:class="regionClasses(location.id)"
						:style="{ fill: slice.fill }"
						:clip-path="`url(#${clipPathId(location.id, index)})`"
						@mouseenter="setCountryHover(location.id, $event)"
					/>
					<path
						:key="`${location.id}-outline`"
						:d="location.path"
						class="world-map-region-outline"
						fill="none"
						stroke="#ffffff"
						stroke-width="0.5"
						pointer-events="none"
					/>
				</template>
				<path
					v-else
					:key="location.id"
					:d="location.path"
					class="world-map-region"
					:class="regionClasses(location.id)"
					:style="{ fill: getRegionFill(location.id) }"
					@mouseenter="setCountryHover(location.id, $event)"
				/>
			</template>
		</svg>

		<div
			v-if="hoveredCountry"
			class="map-tooltip"
			:style="tooltipStyle"
			@mouseenter="cancelClearHover"
			@mouseleave="scheduleClearHover"
		>
			<p class="map-tooltip-title mb-1">
				{{ hoveredCountry.name }}
				<span v-if="hoveredCountry.suppliers.length > 1" class="map-tooltip-count">
					({{ hoveredCountry.suppliers.length }} suppliers)
				</span>
			</p>
			<p
				v-if="hoveredCountry.fillMode === 'split'"
				class="map-tooltip-note mb-2"
			>
				Split by supplier score: lowest on the left, highest on the right.
			</p>
			<p class="map-tooltip-average mb-2">
				Average score:
				<strong>{{ hoveredCountry.averageLabel }}</strong>
			</p>
			<ul class="map-tooltip-list mb-0 pl-0">
				<li
					v-for="supplier in hoveredCountry.suppliers"
					:key="`${supplier.name}-${supplier.clientID || supplier.score}`"
				>
					<router-link
						v-if="supplier.clientID"
						:to="supplierLink(supplier.clientID)"
						class="map-tooltip-link"
					>
						{{ supplier.name }}
					</router-link>
					<span v-else>{{ supplier.name }}</span>
					- {{ supplier.score }}
				</li>
			</ul>
		</div>
	</div>
</template>

<script>
import world from "@svg-maps/world"
import { MAP_TIER_COLORS } from "../utils/connectionMapChart"

export default {
	name: "SupplierPerformanceWorldMap",
	props: {
		countries: {
			type: Array,
			default: () => [],
		},
	},
	data() {
		return {
			world,
			hoveredCountryId: null,
			tooltipX: 0,
			tooltipY: 0,
			hideTooltipTimer: null,
		}
	},
	computed: {
		countryById() {
			return this.countries.reduce((map, country) => {
				map[country.id] = country
				return map
			}, {})
		},
		splitCountries() {
			return this.countries.filter((country) => country.fillMode === "split")
		},
		splitClipPaths() {
			const clips = []

			for (const country of this.splitCountries) {
				const sliceCount = country.splitSlices?.length || 0
				for (let index = 0; index < sliceCount; index += 1) {
					clips.push({
						key: `${country.id}-${index}`,
						id: this.clipPathId(country.id, index),
						x: index / sliceCount,
						width: 1 / sliceCount,
					})
				}
			}

			return clips
		},
		hoveredCountry() {
			if (!this.hoveredCountryId) {
				return null
			}
			return this.countryById[this.hoveredCountryId] || null
		},
		tooltipStyle() {
			return {
				left: `${this.tooltipX}px`,
				top: `${this.tooltipY}px`,
			}
		},
	},
	beforeDestroy() {
		this.cancelClearHover()
	},
	methods: {
		clipPathId(countryId, index) {
			return `supplier-map-${countryId}-${index}`
		},
		isSplitCountry(locationId) {
			const country = this.countryById[locationId]
			return (
				country?.fillMode === "split" &&
				Array.isArray(country.splitSlices) &&
				country.splitSlices.length >= 2
			)
		},
		getSplitSlices(locationId) {
			return this.countryById[locationId]?.splitSlices || []
		},
		regionClasses(locationId) {
			return {
				"world-map-region--active": Boolean(this.countryById[locationId]),
				"world-map-region--hovered": this.hoveredCountryId === locationId,
			}
		},
		getRegionFill(locationId) {
			const country = this.countryById[locationId]
			if (!country) {
				return MAP_TIER_COLORS.default
			}
			return country.fill
		},
		setCountryHover(locationId, event) {
			if (!this.countryById[locationId]) {
				return
			}
			this.cancelClearHover()
			this.hoveredCountryId = locationId
			if (event && this.$el) {
				const bounds = this.$el.getBoundingClientRect()
				this.tooltipX = event.clientX - bounds.left + 12
				this.tooltipY = event.clientY - bounds.top + 12
			}
		},
		scheduleClearHover() {
			this.cancelClearHover()
			this.hideTooltipTimer = window.setTimeout(() => {
				this.hoveredCountryId = null
				this.hideTooltipTimer = null
			}, 250)
		},
		cancelClearHover() {
			if (this.hideTooltipTimer) {
				window.clearTimeout(this.hideTooltipTimer)
				this.hideTooltipTimer = null
			}
		},
		supplierLink(clientID) {
			return {
				name: "Single Client View",
				params: { id: clientID },
				query: { focus: "performance" },
			}
		},
	},
}
</script>

<style scoped>
.supplier-performance-world-map {
	position: relative;
	width: 100%;
	min-height: 441px;
}

.world-map-svg {
	display: block;
	width: 100%;
	height: auto;
}

.world-map-region {
	stroke: #ffffff;
	stroke-width: 0.5;
	transition: fill 0.15s ease, opacity 0.15s ease;
}

.world-map-region--split {
	stroke-width: 0;
}

.world-map-region--active {
	cursor: pointer;
}

.world-map-region--active:hover,
.world-map-region--hovered {
	opacity: 0.88;
	filter: brightness(0.92);
}

.map-tooltip {
	position: absolute;
	z-index: 2;
	max-width: 260px;
	padding: 12px 14px;
	border: 1px solid rgba(0, 0, 0, 0.12);
	border-radius: 8px;
	background: #ffffff;
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
	pointer-events: auto;
}

.map-tooltip-link {
	color: #1e7c83;
	font-weight: 600;
	text-decoration: none;
}

.map-tooltip-link:hover {
	text-decoration: underline;
}

.map-tooltip-title {
	font-size: 1rem;
	font-weight: 600;
	color: #1a1a1a;
}

.map-tooltip-count {
	font-size: 0.8125rem;
	font-weight: 500;
	color: #757575;
}

.map-tooltip-note {
	font-size: 0.8125rem;
	color: #757575;
	line-height: 1.4;
}

.map-tooltip-average {
	font-size: 0.875rem;
	color: #424242;
}

.map-tooltip-list {
	list-style: none;
	font-size: 0.8125rem;
	color: #616161;
	line-height: 1.5;
}
</style>
